/**
 * Projects and phases are trees three levels deep (migration 0091; the #55
 * plan's §C.7). The database decides (`tree_fits`); these say the same thing
 * on screen, so a parent picker offers only the parents a save would take,
 * and a list names each row by its path.
 */

export interface TreeRow {
  id: string;
  parentId: string | null;
  name: string;
}

export const TREE_DEPTH = 3;

/** The row's level, 1 at the top. */
export function depthOf(rows: TreeRow[], id: string): number {
  const byId = new Map(rows.map((row) => [row.id, row]));
  let depth = 0;
  let current = byId.get(id);
  while (current !== undefined && depth < 16) {
    depth += 1;
    current = current.parentId === null ? undefined : byId.get(current.parentId);
  }
  return depth;
}

/** 1 for a row with nothing under it, plus the deepest of what is. */
export function heightOf(rows: TreeRow[], id: string): number {
  const children = rows.filter((row) => row.parentId === id);
  return 1 + children.reduce((deepest, child) => Math.max(deepest, heightOf(rows, child.id)), 0);
}

/** Whether `candidate` is `id` or sits somewhere under it. */
export function isWithin(rows: TreeRow[], candidate: string, id: string): boolean {
  const byId = new Map(rows.map((row) => [row.id, row]));
  let current = byId.get(candidate);
  for (let steps = 0; current !== undefined && steps < 16; steps += 1) {
    if (current.id === id) return true;
    current = current.parentId === null ? undefined : byId.get(current.parentId);
  }
  return false;
}

/** The parents `self` (null for a new row) may go under: never itself or its own sub-tree, and never past the third level. */
export function parentsFor<T extends TreeRow>(rows: T[], self: string | null): T[] {
  const height = self === null ? 1 : heightOf(rows, self);
  return rows.filter(
    (row) => (self === null || !isWithin(rows, row.id, self)) && depthOf(rows, row.id) + height <= TREE_DEPTH,
  );
}

/** "Subdivision › Lot 12 › Unit B". */
export function pathOf(rows: TreeRow[], id: string): string {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const names: string[] = [];
  let current = byId.get(id);
  for (let steps = 0; current !== undefined && steps < 16; steps += 1) {
    names.unshift(current.name);
    current = current.parentId === null ? undefined : byId.get(current.parentId);
  }
  return names.join(' › ');
}
